import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthModalStore, toast } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { apiFetch, type AuthResult } from "@/lib/api";
import { friendlyAuthError } from "@/lib/errors";

const GOOGLE_CLIENT_ID =
  (import.meta as unknown as { env?: { VITE_GOOGLE_CLIENT_ID?: string } }).env
    ?.VITE_GOOGLE_CLIENT_ID ||
  "868359307131-v8flss969d3dovamg2tp0ucd462ja0si.apps.googleusercontent.com";

const TERMS_URL =
  "https://terms.alicdn.com/legal-agreement/terms/b_end_product_protocol/20231117105153997/20231117105153997.html";
const PRIVACY_URL =
  "https://terms.alicdn.com/legal-agreement/terms/privacy_policy_full/20240119103118867/20240119103118867.html";

let googleScriptPromise: Promise<void> | null = null;

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            locale?: string;
          }) => void;
          renderButton: (
            element: HTMLElement,
            options: Record<string, string | number | boolean>,
          ) => void;
        };
      };
    };
  }
}

export function AuthModal() {
  const open = useAuthModalStore((s) => s.open);
  const close = useAuthModalStore((s) => s.close);

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : close())}>
      <DialogContent className="max-w-[420px] overflow-hidden p-8" aria-describedby={undefined}>
        <DialogTitle className="sr-only">登录</DialogTitle>
        <LoginPane onSuccess={close} />
      </DialogContent>
    </Dialog>
  );
}

function LoginPane({ onSuccess }: { onSuccess: () => void }) {
  const [phone, setPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsHint, setSmsHint] = useState("");
  const [smsCountdown, setSmsCountdown] = useState(0);
  const setAuth = useAuthStore((s) => s.setAuth);

  useEffect(() => {
    if (smsCountdown <= 0) return;
    const timer = window.setTimeout(() => {
      setSmsCountdown((value) => Math.max(value - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [smsCountdown]);

  const sendSms = useMutation({
    mutationFn: () =>
      apiFetch<{ dev_sms_code?: string; cooldown_seconds: number }>(
        "/api/v2/auth/sms-code",
        { method: "POST", auth: false, body: { phone, scene: "login" } },
      ),
    onSuccess: (data) => {
      setSmsCountdown(Number(data.cooldown_seconds || 60));
      setSmsHint(
        data.dev_sms_code
          ? `开发环境验证码：${data.dev_sms_code}，${data.cooldown_seconds} 秒后可重发。`
          : `验证码已发送，${data.cooldown_seconds} 秒后可重发。`,
      );
      toast("验证码已发送");
    },
    onError: (error: Error) => toast(friendlyAuthError(error.message, "sms")),
  });

  const smsLogin = useMutation({
    mutationFn: () =>
      apiFetch<AuthResult>("/api/v2/auth/login-sms", {
        method: "POST",
        auth: false,
        body: { phone, sms_code: smsCode },
      }),
    onSuccess: (data) => {
      onSuccess();
      setAuth(data);
      toast("登录成功");
    },
    onError: (error: Error) => toast(friendlyAuthError(error.message, "login")),
  });

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <h3 className="text-xl font-semibold tracking-tight text-foreground">登录</h3>
        <p className="text-sm text-muted-foreground">
          未注册的手机，通过验证后将自动注册账号
        </p>
      </div>

      <form
        className="space-y-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          smsLogin.mutate();
        }}
      >
        <div className="flex h-11 overflow-hidden rounded-md border border-border bg-input transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
          <div className="grid w-14 place-items-center border-r border-border text-sm font-medium text-foreground">
            +86
          </div>
          <Input
            id="login-phone"
            type="tel"
            placeholder="请输入手机号"
            value={phone}
            onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
            autoComplete="tel"
            inputMode="numeric"
            className="h-full rounded-none border-0 bg-transparent focus-visible:border-0 focus-visible:ring-0"
            required
          />
        </div>

        <div className="flex h-11 overflow-hidden rounded-md border border-border bg-input transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
          <Input
            id="login-sms"
            type="text"
            placeholder="请输入验证码"
            value={smsCode}
            onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoComplete="one-time-code"
            inputMode="numeric"
            className="h-full rounded-none border-0 bg-transparent focus-visible:border-0 focus-visible:ring-0"
            required
          />
          <button
            type="button"
            className="min-w-[104px] border-l border-border px-3 text-sm font-medium text-accent transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:text-muted-foreground"
            disabled={!phone || sendSms.isPending || smsCountdown > 0}
            onClick={() => sendSms.mutate()}
          >
            {sendSms.isPending
              ? "发送中"
              : smsCountdown > 0
                ? `${smsCountdown}秒后重发`
                : "获取验证码"}
          </button>
        </div>

        <Button type="submit" className="w-full" size="lg" disabled={smsLogin.isPending}>
          {smsLogin.isPending ? "登录中..." : "登录"}
        </Button>
      </form>

      {smsHint && (
        <p className="text-center text-xs text-muted-foreground/70">{smsHint}</p>
      )}

      <AuthDivider />
      <GoogleAuthButton onSuccess={onSuccess} />
      <AgreementText />
    </div>
  );
}

function GoogleAuthButton({ onSuccess }: { onSuccess: () => void }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mutateRef = useRef<(credential: string) => void>(() => {});
  const [scriptReady, setScriptReady] = useState(false);
  const [scriptFailed, setScriptFailed] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);

  const googleAuth = useMutation({
    mutationFn: (credential: string) =>
      apiFetch<AuthResult>("/api/v2/auth/google", {
        method: "POST",
        auth: false,
        body: { credential },
      }),
    onSuccess: (data) => {
      onSuccess();
      setAuth(data);
      toast("登录成功");
    },
    onError: (error: Error) => toast(friendlyAuthError(error.message, "login")),
  });

  mutateRef.current = (credential: string) => googleAuth.mutate(credential);

  useEffect(() => {
    let cancelled = false;
    loadGoogleIdentityScript()
      .then(() => {
        if (!cancelled) setScriptReady(true);
      })
      .catch(() => {
        if (!cancelled) setScriptFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!scriptReady || !host || !window.google?.accounts?.id) return;
    host.innerHTML = "";
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      locale: "zh-CN",
      callback: (response) => {
        if (!response.credential) {
          toast("Google 登录失败，请重试");
          return;
        }
        mutateRef.current(response.credential);
      },
    });
    window.google.accounts.id.renderButton(host, {
      type: "standard",
      theme: "outline",
      size: "large",
      shape: "rectangular",
      text: "continue_with",
      logo_alignment: "left",
      locale: "zh-CN",
      width: Math.min(360, host.clientWidth || 360),
    });
  }, [scriptReady]);

  return (
    <div className="space-y-2">
      <div
        ref={hostRef}
        className="flex min-h-11 w-full justify-center"
        aria-label="使用 Google 账号继续"
      />
      {!scriptReady && !scriptFailed && (
        <div className="grid h-11 place-items-center rounded-md border border-border text-sm text-muted-foreground">
          Google 登录加载中...
        </div>
      )}
      {scriptFailed && (
        <div className="rounded-md border border-border px-3 py-2 text-center text-sm text-muted-foreground">
          Google 登录组件加载失败，请使用验证码登录。
        </div>
      )}
      {googleAuth.isPending && (
        <p className="text-center text-xs text-muted-foreground">正在连接 Google...</p>
      )}
    </div>
  );
}

function AuthDivider() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="text-[11px] text-muted-foreground/75">或</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function AgreementText() {
  return (
    <p className="text-center text-xs leading-relaxed text-muted-foreground">
      登录即代表我已同意
      <a
        href={TERMS_URL}
        target="_blank"
        rel="noreferrer"
        className="text-foreground underline-offset-4 hover:underline"
      >
        用户协议
      </a>
      、
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noreferrer"
        className="text-foreground underline-offset-4 hover:underline"
      >
        隐私协议
      </a>
    </p>
  );
}

function normalizePhoneInput(value: string) {
  const digits = value.replace(/\D/g, "");
  if (/^861[3-9]\d{9}$/.test(digits)) return digits.slice(2);
  return digits.slice(0, 11);
}

function loadGoogleIdentityScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return googleScriptPromise;
}
